/**
 * Stock arithmetic and the negative-stock guard.
 *
 * See specs/001-wine-cellar-tracker/spec.md FR-011/FR-014/SC-003 and
 * data-model.md "Wine" > Derived: stock is never stored, only computed, and
 * must never be allowed to go below zero.
 */

export interface StockInputs {
  purchases: { quantity: number }[];
  tastings: DrunkInputs[];
}

export interface DrunkInputs {
  consumedFromStock: boolean;
  bottles?: number;
}

/**
 * Bottles a tasting took from stock: none outside the cellar, else its `bottles` (schema
 * version 7, owner 2026-10-09), one when it has none.
 */
export function bottlesDrunk(tasting: DrunkInputs): number {
  return tasting.consumedFromStock ? (tasting.bottles ?? 1) : 0;
}

/** Total bottles purchased minus total bottles drunk from stock. */
export function computeStock(inputs: StockInputs): number {
  const purchased = inputs.purchases.reduce((sum, p) => sum + p.quantity, 0);
  const drunk = inputs.tastings.reduce((sum, t) => sum + bottlesDrunk(t), 0);
  return purchased - drunk;
}

/** Thrown by any change that would leave a wine's stock below zero. */
export class StockError extends Error {
  constructor(message = 'Cette modification ferait passer le stock sous zéro.') {
    super(message);
    this.name = 'StockError';
  }
}

/** Throws StockError if `stock` is negative; a stock of exactly 0 is fine. */
export function ensureStockNotNegative(stock: number): void {
  if (stock < 0) {
    throw new StockError();
  }
}
